/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit, ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  Self,
  SimpleChanges,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';

import { TranslationService } from '@gh/core-mls';
import { KEY_ESCAPE } from '@gh/core-ui';
import { getScrollbarHeight, getScrollbarWidth, notNil } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';

import {
  Autowired,
  Bean,
  CellDoubleClickedEvent,
  CellEditingStartedEvent,
  ColumnApi,
  ComponentUtil,
  DragAndDropService,
  GetRowNodeIdFunc,
  Grid,
  GridApi,
  GridOptions,
  GridOptionsWrapper,
  GridParams,
  GridReadyEvent,
  PostConstruct,
  RowNode,
} from 'ag-grid-community';
import { assign, find, isEmpty, isString, map, noop, over, property, remove, sortBy } from 'lodash';
import { Subject } from 'rxjs';

import {
  GridCellEditorFactory,
  GridCellRendererFactory,
  GridFrameworkComponentWrapper,
  GridFrameworkFactory,
  GridHeaderComponentFactory,
} from '../../cell-components';
import { GRID_FEATURE, GridFeature, GridFeatureReadyParams, GridNoRowsOverlayDirective } from '../../features';
import { GridRowEvent } from '../../grid-events';
import { ICON_SET } from '../../icon-set';
import { GridLayout, GridDefinition, GridDefinitionManager } from '../../layout';
import { GridColumnComponent } from '../grid-column/grid-column.component';
import { GridOptionBarComponent } from '../grid-option-bar/grid-option-bar.component';
import { GridSelectBarComponent } from '../grid-select-bar/grid-select-bar.component';
import { createLoadingOverlay, createNoRowsOverlay } from './grid-overlay-definitions';

const THEME_CONFIG = {
  typical: {
    headerHeight: 40,
    rowHeight: 35,
  },
  narrow: {
    headerHeight: 31,
    rowHeight: 26,
  },
  sparsed: {
    headerHeight: 60,
    rowHeight: 55,
    rowSelection: 'multiple',
  },
};

const STOPPED_KEYS = [KEY_ESCAPE];

function getRowNodeIdFunc(idProperty: string | GetRowNodeIdFunc): GetRowNodeIdFunc {
  return isString(idProperty) ? property(idProperty) : idProperty;
}

/**
 * This class allows to access private ag-grid services
 */

@Component({
  selector: 'gh-grid',
  template: require('./grid.component.html'),
  styles: [require('./grid.component.scss')],
  providers: [
    GridFrameworkFactory,
    GridFrameworkComponentWrapper,
    GridCellRendererFactory,
    GridCellEditorFactory,
    GridHeaderComponentFactory,
    GridDefinitionManager,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None, // tslint:disable-line:use-view-encapsulation
  exportAs: 'ghGrid',
})
export class GridComponent implements AfterContentInit, OnChanges, OnInit, OnDestroy {

  @Input() theme = 'typical';
  @Input() rowClass: any;
  @Input() dragRow = false;
  @Input() selectable = false;
  @Input() selectionPrimaryKey = 'id';
  @Input() hasSettings = true;
  @Input() sorting = true;
  @Input() useNoRowsOverlay = true;
  @Input() noRowsTitle = this.translationService.get('lbl_you_have_no_entries');
  @Input() idProperty?: string | GetRowNodeIdFunc;
  @Input() headless = false;

  @Output() rowClicked = new EventEmitter<GridRowEvent<any>>();
  @Output() cellDoubleClicked = new EventEmitter<CellDoubleClickedEvent>();
  @Output() cellEditingStarted = new EventEmitter<CellEditingStartedEvent>();
  @Output() gridReady = new EventEmitter<GridReadyEvent>();
  @Output() layoutChange: EventEmitter<GridLayout>;

  @ContentChildren(GridColumnComponent) columns: QueryList<GridColumnComponent>;
  @ContentChild(GridOptionBarComponent, { static: false }) optionBar: GridOptionBarComponent;
  @ContentChild(GridSelectBarComponent, { static: false }) selectBar: GridSelectBarComponent;
  @ContentChild(GridNoRowsOverlayDirective, { static: false }) noRowsOverlay?: GridNoRowsOverlayDirective;

  @HostBinding('class.stretch-container')
  classStretchContainer = true;

  initialized: boolean = false;
  api: GridApi;
  ready$$: Subject<any> = new Subject();
  selectedRows: any[] = [];

  onReady = this.ready$$.asObservable().pipe(map$(() => ({ api: this.api, columnApi: this.columnApi })));

  private _sizeColumnsToFit: boolean;
  private grid: Grid;
  private options: GridOptions;
  private gridParams: GridParams;
  private columnApi: ColumnApi;
  private destroyed: boolean = false;
  private gridEl: HTMLElement;
  private _pinnedTopData: any[];
  private _pinnedBottomData: any[];
  private _loading: boolean = false;
  private sharedBeans: GridSharedBeans;

  private bodyEl: HTMLElement;
  private overlayEl: HTMLElement;

  constructor(public elementRef: ElementRef,
              private viewContainerRef: ViewContainerRef,
              private componentFactoryResolver: ComponentFactoryResolver,
              private renderer: Renderer2,
              private gridFrameworkFactory: GridFrameworkFactory,
              private frameworkComponentWrapper: GridFrameworkComponentWrapper,
              private translationService: TranslationService,
              private definitionManager: GridDefinitionManager,
              @Optional() @Self() @Inject(GRID_FEATURE) private features: GridFeature[] = []) {
    if (!this.features) {
      this.features = [];
    }
    this.layoutChange = this.definitionManager.layoutChange;
  }

  @Input()
  set sizeColumnsToFit(sizeColumnsToFit: any) {
    this._sizeColumnsToFit = coerceBooleanProperty(sizeColumnsToFit);
  }

  @Input()
  set definition(definition: GridDefinition) {
    this.definitionManager.update(definition);

    if (this.initialized) {
      this.mergeDefinition();
    }
  }

  @Input()
  set pinnedTopData(data: any[]) {
    if (this.api) {
      this.api.setPinnedTopRowData(data);

      this.fixOverlayPosition();
    }
    this._pinnedTopData = data;
  }

  @Input()
  set pinnedBottomData(data: any[]) {
    if (this.api) {
      this.api.setPinnedBottomRowData(data);

      this.fixOverlayPosition();
    }
    this._pinnedBottomData = data;
  }

  @Input()
  set loading(loading: boolean) {
    this._loading = loading;

    if (this.api) {
      this.updateLoadingOverlay();
    }
  }

  get displaySettings(): boolean {
    return this.definitionManager.dynamicLayout && this.hasSettings && this.definitionManager.hasLayout;
  }

  ngOnInit(): void {
    this.gridEl = this.elementRef.nativeElement.querySelector('#grid');

    this.renderer.addClass(this.elementRef.nativeElement, 'gh-ag-grid');
    this.renderer.addClass(this.elementRef.nativeElement, `gh-grid-${this.theme}-theme`);
    if (this.headless) {
      this.renderer.addClass(this.elementRef.nativeElement, `gh-grid-headless`);
    }
    if (this.selectable) {
      this.renderer.addClass(this.elementRef.nativeElement, 'selectable');
    }
    this.renderer.addClass(this.gridEl, 'ag-theme-material');

    this.gridFrameworkFactory.setViewContainerRef(this.viewContainerRef);
    this.frameworkComponentWrapper.setComponentFactoryResolver(this.componentFactoryResolver);
    this.frameworkComponentWrapper.setViewContainerRef(this.viewContainerRef);
  }

  ngOnDestroy(): void {
    if (this.initialized) {
      this.destroyFeatures();
      this.definitionManager.destroy();
      this.destroyed = true;
      this.api.destroy();
      this.ready$$.complete();
    }
  }

  ngAfterContentInit(): void {
    this.definitionManager.columns = this.columns.map((c) => c);
    this.initFeatures();
    const themeConfig = THEME_CONFIG[this.theme];
    this.options = {
      headerHeight: themeConfig.headerHeight,
      rowHeight: themeConfig.rowHeight,
      getRowClass: this.rowClass,
      suppressMovableColumns: true,
      rowDragManaged: this.dragRow,
      animateRows: true,
      /**
       * stopEditingWhenGridLosesFocus should not be set to true,
       * because it prevents applying of value selected in autocomplete.
       *
       * This happens because autocomplete displays its options in overlay,
       * when user clicks on autocomplete option this click is made outside of grid body
       * and grid thinks that it loosed focus. This way selected value is not applied.
       *
       * We can reconsider this solution in future
       * if ag-grid will provide any possibility to manage this behavior manually.
       */
      // stopEditingWhenGridLosesFocus: true,
      defaultColDef: {
        resizable: true,
        sortable: this.sorting,
        // comparator: () => 0,
      },
      getRowNodeId: this.idProperty ? getRowNodeIdFunc(this.idProperty) : void 0,
      rowData: [],
      icons: {
        groupExpanded: ICON_SET.GROUP_EXPANDED,
        groupContracted: ICON_SET.GROUP_CONTRACTED,
        sortAscending: ICON_SET.SORT_ASCENDING,
        sortDescending: ICON_SET.SORT_DESCENDING,
        externalFilter: ICON_SET.EXTERNAL_FILTER,
      },
      overlayNoRowsTemplate: this.useNoRowsOverlay ? createNoRowsOverlay(this.noRowsTitle) : ' ',
      overlayLoadingTemplate: createLoadingOverlay(),
      // TODO: We have to set this property, otherwise option and select bars do not work correctly:
      //       New rows rendered due to virtual rendering does not have option and select bars
      suppressAnimationFrame: true,
      onGridReady: this.onGridReady.bind(this),
    };

    this.mergeDefinition();

    this.enhanceOptions();
    this.gridParams = {
      globalEventListener: this.globalEventListener.bind(this),
      frameworkFactory: this.gridFrameworkFactory,
      seedBeanInstances: {
        ...this.getSeedBeans(),
        frameworkComponentWrapper: this.frameworkComponentWrapper,
        // we make GridComponent available in ag-grid injection context
        ghGrid: this,
      },
    };

    this.grid = new Grid(this.gridEl, this.options, this.gridParams);

    // add event listener for Shift keydown selection functionality
    if (this.selectable) {
      this.ready$$.subscribe(() => {
        this.api.addEventListener('rowClicked', (event: any) => {
          if (event.event.shiftKey) {
            this.selectRow(event.data);
          }
        });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized) {
      ComponentUtil.processOnChange(changes, this.options, this.api, this.columnApi);
    }
  }

  openSettings(): void {
    this.definitionManager.openSettings();
  }

  onGridReady(event: GridReadyEvent): void {
    const api = notNil(event.api);
    const columnApi = notNil(event.columnApi);
    assign(this, { api, columnApi, initialized: true });
    this.definitionManager.init(api, columnApi);
    this.readyFeatures({ api, columnApi, sharedBeans: this.sharedBeans });
    this.ready$$.next(this);

    this.bodyEl = this.elementRef.nativeElement.querySelector('.ag-body-viewport');
    this.overlayEl = this.elementRef.nativeElement.querySelector('.ag-overlay');

    if (this._pinnedTopData) {
      api.setPinnedTopRowData(this._pinnedTopData);
    }

    if (this._pinnedBottomData) {
      api.setPinnedBottomRowData(this._pinnedBottomData);
    }

    if (this._sizeColumnsToFit) {
      columnApi.sizeColumnsToFit(this.getPreferredBodyWidth());
    }

    if (this.headless) {
      api.setHeaderHeight(0);
    }

    if (this._loading) {
      this.updateLoadingOverlay();
    }

    this.fixOverlayPosition();
  }

  selectRow(item: any): void {
    const { selectionPrimaryKey } = this;
    const selectedPrimaryKeys = map(this.selectedRows, ({ [selectionPrimaryKey]: id }) => id);
    this.api.forEachNodeAfterFilter((node: RowNode) => {
      if (node.data[selectionPrimaryKey] === item[selectionPrimaryKey]) {
        const alreadySelected = selectedPrimaryKeys.includes(node.data[selectionPrimaryKey]);
        if (alreadySelected) {
          remove(this.selectedRows, ({ [selectionPrimaryKey]: id }: any) => id === node.data[selectionPrimaryKey]);
        } else {
          this.selectedRows.push(node.data);
        }
        node.setSelected(!alreadySelected);
      }
    });
  }

  selectAllRows(): void {
    this.api.selectAll();
    assign(this, { selectedRows: this.api.getSelectedRows() });
  }

  deselectAllRows(): void {
    this.api.deselectAll();
    assign(this, { selectedRows: [] });
  }

  isRowSelected(item: any): boolean {
    const { selectionPrimaryKey } = this;
    return !!find(this.selectedRows, (row: any) => row.data[selectionPrimaryKey] === item[selectionPrimaryKey]);
  }

  isSelectedPartial(): boolean {
    return this.api
      && !isEmpty(this.selectedRows)
      && this.selectedRows.length !== this.api.getModel().getRowCount();
  }

  isSelectedAll(): boolean {
    return this.api && !isEmpty(this.selectedRows) && !this.isSelectedPartial();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // stop some grid specific key presses, like Escape
    // escape should not close dialog when editable grid embedded into dialog
    // if (STOPPED_KEYS.includes(event.keyCode)) {
    //   console.log(77777777)
    //   event.stopPropagation();
    // }
  }

  setSharedBeans(sharedBeans: GridSharedBeans): void {
    this.sharedBeans = sharedBeans;
  }

  private globalEventListener(eventType: string, event: any): void {
    // if we are tearing down, don't emit angular 2 events, as this causes
    // problems with the angular 2 router
    if (this.destroyed) {
      return;
    }
    // generically look up the eventType
    const emitter = <EventEmitter<any>>(<any>this)[eventType];
    if (emitter) {
      emitter.emit(event);
    }
  }

  private initFeatures(): void {
    if (this.optionBar) {
      this.features.push(this.optionBar.getFeature());
    }

    if (this.selectBar) {
      this.features.push(this.selectBar.getFeature());
    }

    if (this.noRowsOverlay) {
      this.features.push(this.noRowsOverlay.getFeature());
    }

    this.features = sortBy(this.features, (feature) => feature.order || 0);

    this.features.forEach((feature) => feature.initGrid({
      viewContainerRef: this.viewContainerRef,
      nativeElement: this.gridEl,
    }));
  }

  private enhanceOptions(): void {
    const { features } = this;
    if (!isEmpty(features)) {
      const compactFeatureProcesses = (feature: GridFeature) =>
        (params: any) => feature.process ? feature.process(params) : noop;
      const processRowPostCreate = over(map(features, compactFeatureProcesses), <any>this.options.processRowPostCreate);
      assign(this.options, { processRowPostCreate });
    }
    this.features.forEach((feature) => feature.enhanceGridOptions(this.options));
    this.definitionManager.afterGridOptionsEnhanced(this.options);
  }

  private getSeedBeans(): Hash<any> {
    return this.features.reduce(
      (beans, feature) => feature.seedBeans ? assign(beans, feature.seedBeans) : beans,
      {});
  }

  private readyFeatures(params: GridFeatureReadyParams): void {
    this.features.forEach((feature) => feature.onGridReady(params));
  }

  private destroyFeatures(): void {
    this.features.forEach((feature) => feature.onDestroy());
  }

  private mergeDefinition(): void {
    const options = this.definitionManager.createOptions();
    assign(this.options, { ...options, defaultColDef: { ...this.options.defaultColDef, ...options.defaultColDef } });

    if (this.api) {
      this.api.setColumnDefs(<any>options.columnDefs);
    }
  }

  private updateLoadingOverlay(): void {
    const { _loading: loading } = this;

    if (loading) {
      this.api.showLoadingOverlay();
    } else {
      this.api.hideOverlay();
    }
  }

  /**
   * Overlay should not overlap pinned rows
   */
  private fixOverlayPosition(): void {
    const bottomPinnedPanelHeight = this.api.getPinnedBottomRowCount() * THEME_CONFIG[this.theme].rowHeight
      + getScrollbarHeight();
    this.overlayEl.style.bottom = `${bottomPinnedPanelHeight}px`;
  }

  /**
   * Returns preferred width of the body.
   * By default grid calculates its width for sizeColumnsToFit based on the current state (with or without scrollbar).
   * This method calculates width supposing that scrollbar is shown:
   * this helps to avoid the case when scrollbar is shown for data loaded later.
   */
  private getPreferredBodyWidth(): number {
    const vScrollbarWidth = this.options.scrollbarWidth ? this.options.scrollbarWidth : getScrollbarWidth();
    return this.bodyEl.clientWidth - vScrollbarWidth;
  }
}

@Bean('sharedBeans')
export class GridSharedBeans {
  @Autowired('dragAndDropService')
  dragAndDropService: DragAndDropService;
  @Autowired('gridOptionsWrapper')
  gridOptionsWrapper: GridOptionsWrapper;
  @Autowired('ghGrid')
  grid: GridComponent;

  @PostConstruct // tslint:disable-line:prefer-inline-decorator
  init(): void {
    // makes shared beans available in GridComponent
    this.grid.setSharedBeans(this);
  }
}
